/** Query strings decode `+` as space — restore before matching `web+nrm:`. */
const PROTOCOL_PREFIX_RE = /^web\+nrm:(?:\/\/)?/i;
const PROTOCOL_PREFIX_FROM_QUERY_RE = /^web\s+nrm:(?:\/\/)?/i;

export type ResolvedProtocolTarget = {
  path: string;
  locale?: string;
};

function safeDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function stripProtocolPrefix(value: string): string {
  const stripped = value
    .replace(PROTOCOL_PREFIX_RE, '')
    .replace(PROTOCOL_PREFIX_FROM_QUERY_RE, '')
    .trim();

  if (!stripped || /^\/+$/.test(stripped)) {
    return '';
  }

  return stripped;
}

/**
 * Resolves PWA protocol handler `target` (%s) to an app path without locale prefix.
 * %s may be `//ru`, `ru`, `web+nrm://`, `web+nrm://ru/...`, or a same-origin https URL.
 */
export function resolveProtocolTarget(
  target: string,
  localeCodes: string[],
): ResolvedProtocolTarget {
  const decoded = safeDecodeURIComponent(target).trim();

  if (/^https?:\/\//i.test(decoded)) {
    try {
      const url = new URL(decoded);

      if (import.meta.client && url.origin === globalThis.location.origin) {
        return resolvePathWithLocale(url.pathname + url.search + url.hash, localeCodes);
      }
    } catch {
      /* invalid URL */
    }
  }

  if (/^web(\+|\s)nrm:/i.test(decoded)) {
    return resolvePathWithLocale(stripProtocolPrefix(decoded), localeCodes);
  }

  return resolvePathWithLocale(decoded, localeCodes);
}

function resolvePathWithLocale(
  raw: string,
  localeCodes: string[],
): ResolvedProtocolTarget {
  const trimmed = raw.trim();

  if (!trimmed) {
    return { path: '/' };
  }

  const [pathPart = '', ...queryParts] = trimmed.split('?');
  const query = queryParts.length > 0 ? `?${queryParts.join('?')}` : '';
  const normalized = pathPart.startsWith('/') ? pathPart : `/${pathPart}`;
  const segments = normalized.replace(/^\/+/, '').split('/')
    .filter(Boolean);

  let locale: string | undefined;

  if (segments[0] !== undefined && localeCodes.includes(segments[0])) {
    locale = segments[0];
    segments.shift();
  }

  const path = segments.length > 0 ? `/${segments.join('/')}` : '/';

  return { path: `${path}${query}`, locale };
}
